/**
 * Why a `getUserMedia` request failed, in words the user can act on.
 *
 * A refusal is two situations wearing one NotAllowedError: a DISMISSED prompt
 * (Esc, click-away) leaves the permission at "prompt" and the next request
 * prompts again, while an explicit Block sticks until the user clears it in
 * site settings. Only the Permissions API can tell them apart, and it can't be
 * trusted to — WebKit's support for the "microphone" name varies by version and
 * doesn't reliably track state changes, so the browsers where a wrong answer
 * costs the most are the ones most likely to give one. One message that holds
 * in both cases beats a precise message that's precise on some platforms and
 * confidently wrong on others.
 *
 * Dependency-free on purpose — the composer's voice button imports this
 * eagerly, so anything pulled in here lands in the chat bundle.
 */

const MIC_REFUSED_MESSAGE =
  "Microphone access was denied. Allow it when asked, or turn it on in your browser settings, then try again."

type MicAccessErrorKind =
  | "permission"
  | "missing_device"
  | "unavailable_device"
  | "security"
  | "unsupported"
  | "unknown"

function domErrorName(error: unknown): string | null {
  if (error && typeof error === "object" && "name" in error) {
    const name = error.name
    return typeof name === "string" ? name : null
  }
  return null
}

function micAccessErrorKind(error: unknown): MicAccessErrorKind {
  switch (domErrorName(error)) {
    case "NotAllowedError":
    case "PermissionDeniedError":
      return "permission"
    case "NotFoundError":
    case "DevicesNotFoundError":
      return "missing_device"
    case "NotReadableError":
    case "TrackStartError":
      return "unavailable_device"
    case "SecurityError":
      return "security"
    case "VoiceMicrophoneUnsupportedError":
      return "unsupported"
    default:
      return "unknown"
  }
}

/**
 * Expected customer-environment failures receive actionable UI without an
 * engineering report.
 */
export function isExpectedMicAccessError(error: unknown): boolean {
  const kind = micAccessErrorKind(error)
  return (
    kind === "permission" ||
    kind === "missing_device" ||
    kind === "unavailable_device" ||
    kind === "unsupported"
  )
}

export function isUnknownMicAccessError(error: unknown): boolean {
  return micAccessErrorKind(error) === "unknown"
}

/**
 * Actionable copy for a recognized `getUserMedia` failure. Callers supply the
 * fallback for unknown capture failures.
 */
export function micAccessErrorMessage(error: unknown): string | null {
  switch (micAccessErrorKind(error)) {
    case "permission":
      return MIC_REFUSED_MESSAGE
    case "missing_device":
      return "No microphone was found. Connect a microphone and try again."
    case "unavailable_device":
      return "Your microphone is already in use by another app."
    case "security":
      return "Microphone access is blocked for this page."
    case "unsupported":
      return "This browser does not support microphone access."
    case "unknown":
      return null
  }
}
